import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { projectAtom } from '@data/auth';
import { environmentAtom } from '@data/fms/environment/states';
import type {
  EnvironmentErrorAlertCondition,
  WebAutoApplicationEnvironmentOption,
} from '@data/fms/environment/types';
import { useNotification } from '@data/notification';
import type { AxiosError, AxiosResponse } from 'axios';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

type EnableBatteryAlarmBody = {
  path: '/enable_battery_alarm';
  value: boolean;
};

type BatteryAlarmThresholdBody = {
  path: '/battery_alarm_threshold';
  value: number;
};

type EnableWeatherAlarmBody = {
  path: '/enable_weather_forecast_notification';
  value: boolean;
};

type WeatherAlarmHoursThresholdBody = {
  path: '/weather_forecast_after_hours_threshold';
  value: number;
};

type WeatherAlarmPrecipitationThresholdBody = {
  path: '/weather_forecast_precipitation_threshold';
  value: number;
};

type EnableStuckAlarmBody = {
  path: '/enable_stuck_alarm';
  value: boolean;
};

type StuckAlarmThresholdBody = {
  path: '/stuck_alarm_threshold';
  value: number;
};

type ErrorAlertConditionBody = {
  path: '/error_alert_condition';
  value: EnvironmentErrorAlertCondition;
};

type EnableTelemetryArrivalErrorAlarmBody = {
  path: '/enable_telemetry_arrival_error_alarm';
  value: boolean;
};

type Body =
  | EnableBatteryAlarmBody
  | BatteryAlarmThresholdBody
  | EnableWeatherAlarmBody
  | WeatherAlarmHoursThresholdBody
  | WeatherAlarmPrecipitationThresholdBody
  | EnableStuckAlarmBody
  | StuckAlarmThresholdBody
  | ErrorAlertConditionBody
  | EnableTelemetryArrivalErrorAlarmBody;

/**
 * 走行環境オプション情報変更API
 * - バッテリー通知 ON/OFF 変更
 * - バッテリー通知閾値変更
 * - 天気予報通知 ON/OFF 変更
 * - 天気予報通知時間変更
 * - 天気予報通知降水量変更
 * - 立ち往生通知 ON/OFF 変更
 * - 立ち往生通知閾値変更
 * - エラー通知設定変更
 */
export const usePatchWebAutoApplicationEnvironmentOptionAPI = (): {
  state: APIState;
  patchWebAutoApplicationEnvironmentOptionEnableBatteryAlarm: (
    enable: boolean,
  ) => Promise<WebAutoApplicationEnvironmentOption | null>;
  patchWebAutoApplicationEnvironmentOptionBatteryAlarmThreshold: (
    threshold: number,
  ) => Promise<WebAutoApplicationEnvironmentOption | null>;
  patchWebAutoApplicationEnvironmentOptionEnableWeatherAlarm: (
    enable: boolean,
  ) => Promise<WebAutoApplicationEnvironmentOption | null>;
  patchWebAutoApplicationEnvironmentOptionWeatherAlarmHoursThreshold: (
    threshold: number,
  ) => Promise<WebAutoApplicationEnvironmentOption | null>;
  patchWebAutoApplicationEnvironmentOptionWeatherAlarmPrecipitationThreshold: (
    threshold: number,
  ) => Promise<WebAutoApplicationEnvironmentOption | null>;
  patchWebAutoApplicationEnvironmentOptionEnableStuckAlarm: (
    enable: boolean,
  ) => Promise<WebAutoApplicationEnvironmentOption | null>;
  patchWebAutoApplicationEnvironmentOptionStuckAlarmThreshold: (
    threshold: number,
  ) => Promise<WebAutoApplicationEnvironmentOption | null>;
  patchWebAutoApplicationEnvironmentOptionErrorAlertCondition: (
    condition: EnvironmentErrorAlertCondition,
  ) => Promise<WebAutoApplicationEnvironmentOption | null>;
  patchWebAutoApplicationEnvironmentOptionEnableTelemetryArrivalErrorAlarm: (
    enable: boolean,
  ) => Promise<WebAutoApplicationEnvironmentOption | null>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError, notifySuccess } = useNotification();
  const fmsAPI = useFMSAPI();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const request = useCallback(
    async (
      projectId: string,
      environmentId: string,
      body: Body,
    ): Promise<Result<WebAutoApplicationEnvironmentOption, AxiosResponse>> => {
      try {
        const res = await fmsAPI.patch(
          `/${projectId}/environments/${environmentId}/web_auto_application_environment_option`,
          [{ ...body, op: 'replace' }],
        );
        return new Success(res?.data);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  /**
   * 車両のテレメトリが届かなくなった場合の通知ON/OFF
   */
  const patchWebAutoApplicationEnvironmentOptionEnableTelemetryArrivalErrorAlarm =
    useAtomCallback(
      useCallback(
        async (get, _, enable: boolean) => {
          const project = get(projectAtom);
          const environment = get(environmentAtom);
          if (!project || !environment) {
            notifyError({
              message: t(
                'api.fms.patch_web_auto_application_environment.enable_telemetry_arrival_error_alarm',
                { status: 'failed' },
              ),
            });
            setState('hasError');
            return null;
          }
          setState('loading');
          const res = await request(project.id, environment.environment_id, {
            path: '/enable_telemetry_arrival_error_alarm',
            value: enable,
          });
          if (!res.value || res.isFailure()) {
            notifyError({
              message: `${t(
                'api.fms.patch_web_auto_application_environment.enable_telemetry_arrival_error_alarm',
                { status: 'failed' },
              )}: ${getErrorMessage(res)}`,
            });
            setState('hasError');
            return null;
          }
          notifySuccess({
            message: t(
              'api.fms.patch_web_auto_application_environment.enable_telemetry_arrival_error_alarm',
              { status: 'successfully' },
            ),
          });
          setState('hasValue');
          return res.value;
        },
        [notifyError, notifySuccess, request, t, getErrorMessage],
      ),
    );

  /**
   * バッテリー通知ON/OFF
   */
  const patchWebAutoApplicationEnvironmentOptionEnableBatteryAlarm =
    useAtomCallback(
      useCallback(
        async (get, _, enable: boolean) => {
          const project = get(projectAtom);
          const environment = get(environmentAtom);
          if (!project || !environment) {
            notifyError({
              message: t(
                'api.fms.patch_web_auto_application_environment.enable_battery_alarm',
                { status: 'failed' },
              ),
            });
            setState('hasError');
            return null;
          }
          setState('loading');
          const res = await request(project.id, environment.environment_id, {
            path: '/enable_battery_alarm',
            value: enable,
          });
          if (!res.value || res.isFailure()) {
            notifyError({
              message: `${t(
                'api.fms.patch_web_auto_application_environment.enable_battery_alarm',
                { status: 'failed' },
              )}: ${getErrorMessage(res)}`,
            });
            setState('hasError');
            return null;
          }
          notifySuccess({
            message: t(
              'api.fms.patch_web_auto_application_environment.enable_battery_alarm',
              { status: 'successfully' },
            ),
          });
          setState('hasValue');
          return res.value;
        },
        [notifyError, notifySuccess, request, t, getErrorMessage],
      ),
    );

  /**
   * バッテリー通知閾値
   */
  const patchWebAutoApplicationEnvironmentOptionBatteryAlarmThreshold =
    useAtomCallback(
      useCallback(
        async (get, _, threshold: number) => {
          const project = get(projectAtom);
          const environment = get(environmentAtom);
          if (!project || !environment) {
            notifyError({
              message: t(
                'api.fms.patch_web_auto_application_environment.battery_alarm_threshold',
                { status: 'failed' },
              ),
            });
            setState('hasError');
            return null;
          }
          setState('loading');
          const res = await request(project.id, environment.environment_id, {
            path: '/battery_alarm_threshold',
            value: threshold,
          });
          if (!res.value || res.isFailure()) {
            notifyError({
              message: `${t(
                'api.fms.patch_web_auto_application_environment.battery_alarm_threshold',
                { status: 'failed' },
              )}: ${getErrorMessage(res)}`,
            });
            setState('hasError');
            return null;
          }
          notifySuccess({
            message: t(
              'api.fms.patch_web_auto_application_environment.battery_alarm_threshold',
              { status: 'successfully' },
            ),
          });
          setState('hasValue');
          return res.value;
        },
        [notifyError, notifySuccess, request, t, getErrorMessage],
      ),
    );

  /**
   * 降水量通知ON/OFF
   */
  const patchWebAutoApplicationEnvironmentOptionEnableWeatherAlarm =
    useAtomCallback(
      useCallback(
        async (get, _, enable: boolean) => {
          const project = get(projectAtom);
          const environment = get(environmentAtom);
          if (!project || !environment) {
            notifyError({
              message: t(
                'api.fms.patch_web_auto_application_environment.enable_weather_forecast_notification',
                { status: 'failed' },
              ),
            });
            setState('hasError');
            return null;
          }
          setState('loading');
          const res = await request(project.id, environment.environment_id, {
            path: '/enable_weather_forecast_notification',
            value: enable,
          });
          if (!res.value || res.isFailure()) {
            notifyError({
              message: `${t(
                'api.fms.patch_web_auto_application_environment.enable_weather_forecast_notification',
                { status: 'failed' },
              )}: ${getErrorMessage(res)}`,
            });
            setState('hasError');
            return null;
          }
          notifySuccess({
            message: t(
              'api.fms.patch_web_auto_application_environment.enable_weather_forecast_notification',
              { status: 'successfully' },
            ),
          });
          setState('hasValue');
          return res.value;
        },
        [notifyError, notifySuccess, request, t, getErrorMessage],
      ),
    );

  /**
   * 時間閾値
   */
  const patchWebAutoApplicationEnvironmentOptionWeatherAlarmHoursThreshold =
    useAtomCallback(
      useCallback(
        async (get, _, threshold: number) => {
          const project = get(projectAtom);
          const environment = get(environmentAtom);
          if (!project || !environment) {
            notifyError({
              message: t(
                'api.fms.patch_web_auto_application_environment.weather_forecast_after_hours_threshold',
                { status: 'failed' },
              ),
            });
            setState('hasError');
            return null;
          }
          setState('loading');
          const res = await request(project.id, environment.environment_id, {
            path: '/weather_forecast_after_hours_threshold',
            value: threshold,
          });
          if (!res.value || res.isFailure()) {
            notifyError({
              message: `${t(
                'api.fms.patch_web_auto_application_environment.weather_forecast_after_hours_threshold',
                { status: 'failed' },
              )}: ${getErrorMessage(res)}`,
            });
            setState('hasError');
            return null;
          }
          notifySuccess({
            message: t(
              'api.fms.patch_web_auto_application_environment.weather_forecast_after_hours_threshold',
              { status: 'successfully' },
            ),
          });
          setState('hasValue');
          return res.value;
        },
        [notifyError, notifySuccess, request, t, getErrorMessage],
      ),
    );

  /**
   * 降水量閾値
   */
  const patchWebAutoApplicationEnvironmentOptionWeatherAlarmPrecipitationThreshold =
    useAtomCallback(
      useCallback(
        async (get, _, threshold: number) => {
          const project = get(projectAtom);
          const environment = get(environmentAtom);
          if (!project || !environment) {
            notifyError({
              message: t(
                'api.fms.patch_web_auto_application_environment.weather_forecast_precipitation_threshold',
                { status: 'failed' },
              ),
            });
            setState('hasError');
            return null;
          }
          setState('loading');
          const res = await request(project.id, environment.environment_id, {
            path: '/weather_forecast_precipitation_threshold',
            value: threshold,
          });
          if (!res.value || res.isFailure()) {
            notifyError({
              message: `${t(
                'api.fms.patch_web_auto_application_environment.weather_forecast_precipitation_threshold',
                { status: 'failed' },
              )}: ${getErrorMessage(res)}`,
            });
            setState('hasError');
            return null;
          }
          notifySuccess({
            message: t(
              'api.fms.patch_web_auto_application_environment.weather_forecast_precipitation_threshold',
              { status: 'successfully' },
            ),
          });
          setState('hasValue');
          return res.value;
        },
        [notifyError, notifySuccess, request, t, getErrorMessage],
      ),
    );

  /**
   * 立ち往生通知ON/OFF
   */
  const patchWebAutoApplicationEnvironmentOptionEnableStuckAlarm =
    useAtomCallback(
      useCallback(
        async (get, _, enable: boolean) => {
          const project = get(projectAtom);
          const environment = get(environmentAtom);
          if (!project || !environment) {
            notifyError({
              message: t(
                'api.fms.patch_web_auto_application_environment.enable_stuck_alarm',
                {
                  status: 'failed',
                },
              ),
            });
            setState('hasError');
            return null;
          }
          setState('loading');
          const res = await request(project.id, environment.environment_id, {
            path: '/enable_stuck_alarm',
            value: enable,
          });
          if (!res.value || res.isFailure()) {
            notifyError({
              message: `${t(
                'api.fms.patch_web_auto_application_environment.enable_stuck_alarm',
                {
                  status: 'failed',
                },
              )}: ${getErrorMessage(res)}`,
            });
            setState('hasError');
            return null;
          }
          notifySuccess({
            message: t(
              'api.fms.patch_web_auto_application_environment.enable_stuck_alarm',
              {
                status: 'successfully',
              },
            ),
          });
          setState('hasValue');
          return res.value;
        },
        [notifyError, notifySuccess, request, t, getErrorMessage],
      ),
    );

  /**
   * 立ち往生通知閾値変更
   */
  const patchWebAutoApplicationEnvironmentOptionStuckAlarmThreshold =
    useAtomCallback(
      useCallback(
        async (get, _, threshold: number) => {
          const project = get(projectAtom);
          const environment = get(environmentAtom);
          if (!project || !environment) {
            notifyError({
              message: t(
                'api.fms.patch_web_auto_application_environment.stuck_alarm_threshold',
                {
                  status: 'failed',
                },
              ),
            });
            setState('hasError');
            return null;
          }
          setState('loading');
          const res = await request(project.id, environment.environment_id, {
            path: '/stuck_alarm_threshold',
            value: threshold,
          });
          if (!res.value || res.isFailure()) {
            notifyError({
              message: `${t(
                'api.fms.patch_web_auto_application_environment.stuck_alarm_threshold',
                {
                  status: 'failed',
                },
              )}: ${getErrorMessage(res)}`,
            });
            setState('hasError');
            return null;
          }
          notifySuccess({
            message: t(
              'api.fms.patch_web_auto_application_environment.stuck_alarm_threshold',
              {
                status: 'successfully',
              },
            ),
          });
          setState('hasValue');
          return res.value;
        },
        [notifyError, notifySuccess, request, t, getErrorMessage],
      ),
    );

  /**
   * エラー通知設定変更
   */
  const patchWebAutoApplicationEnvironmentOptionErrorAlertCondition =
    useAtomCallback(
      useCallback(
        async (get, _, condition: EnvironmentErrorAlertCondition) => {
          const project = get(projectAtom);
          const environment = get(environmentAtom);
          if (!project || !environment) {
            notifyError({
              message: t(
                'api.fms.patch_web_auto_application_environment.error_alert_condition',
                {
                  status: 'failed',
                },
              ),
            });
            setState('hasError');
            return null;
          }
          setState('loading');
          const res = await request(project.id, environment.environment_id, {
            path: '/error_alert_condition',
            value: condition,
          });
          if (!res.value || res.isFailure()) {
            notifyError({
              message: `${t(
                'api.fms.patch_web_auto_application_environment.error_alert_condition',
                {
                  status: 'failed',
                },
              )}: ${getErrorMessage(res)}`,
            });
            setState('hasError');
            return null;
          }
          notifySuccess({
            message: t(
              'api.fms.patch_web_auto_application_environment.error_alert_condition',
              {
                status: 'successfully',
              },
            ),
          });
          setState('hasValue');
          return res.value;
        },
        [notifyError, notifySuccess, request, t, getErrorMessage],
      ),
    );

  return {
    state,
    patchWebAutoApplicationEnvironmentOptionEnableBatteryAlarm,
    patchWebAutoApplicationEnvironmentOptionBatteryAlarmThreshold,
    patchWebAutoApplicationEnvironmentOptionEnableWeatherAlarm,
    patchWebAutoApplicationEnvironmentOptionWeatherAlarmHoursThreshold,
    patchWebAutoApplicationEnvironmentOptionWeatherAlarmPrecipitationThreshold,
    patchWebAutoApplicationEnvironmentOptionEnableStuckAlarm,
    patchWebAutoApplicationEnvironmentOptionStuckAlarmThreshold,
    patchWebAutoApplicationEnvironmentOptionErrorAlertCondition,
    patchWebAutoApplicationEnvironmentOptionEnableTelemetryArrivalErrorAlarm,
  };
};
